(() => {
  if (customElements.get('pollards-subscription-summary')) return;

  class PollardsSubscriptionSummary extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.scope = this.closest('[data-js-product-component]');
      if (!this.form || !this.scope || this.controller) return;
      this.controller = new AbortController();
      this.intro = this.querySelector('[data-pollards-purchase-intro]');
      this.content = this.querySelector('[data-pollards-subscription-content]');
      this.selection = this.querySelector('[data-pollards-subscription-selection]');
      this.terms = this.querySelector('[data-pollards-subscription-terms]');
      this.explicitPlan = Boolean(this.readData()?.selectedPlanId);
      this.scope.addEventListener('change', event => {
        if (event.isTrusted && this.widget?.contains(event.target)) this.purchaseTouched = true;
        this.queueUpdate();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.queueUpdate(), 150);
      }, { capture: true, signal: this.controller.signal });
      this.scope.addEventListener('input', () => this.queueUpdate(), { signal: this.controller.signal });
      this.scope.addEventListener('VARIANT_CHANGE', () => this.queueUpdate(), { capture: true, signal: this.controller.signal });
      document.addEventListener('krown:variant:change', () => this.queueUpdate(), { signal: this.controller.signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target) && !this.intro?.contains(record.target)
          || record.target.closest?.('[data-pollards-subscription-data]'))) this.queueUpdate();
      });
      this.observer.observe(this.scope, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['value', 'checked', 'disabled', 'aria-disabled', 'data-pollards-selection-required']
      });
      this.queueUpdate();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.settleTimer);
      this.intro?.remove();
      this.controller = null;
    }

    queueUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    readData() {
      const source = this.querySelector('[data-pollards-subscription-data]')?.textContent;
      if (!source) return null;
      if (source !== this.dataSource) {
        try {
          this.data = JSON.parse(source);
          this.dataSource = source;
        } catch {
          return null;
        }
      }
      return this.data;
    }

    purchaseRadio(type) {
      if (!this.widget) return null;
      return [...this.widget.querySelectorAll('input[type="radio"]')].find(input => {
        const value = `${input.value} ${input.id} ${input.dataset.purchaseType || ''}`.toLowerCase();
        return type === 'onetime' ? /onetime|one[-_ ]time|rc-radio1\b/.test(value) : /subscription|subscribe|rc-radio2\b/.test(value);
      });
    }

    labelFor(input) {
      if (!input) return null;
      return input.labels?.[0] || input.closest('label') || input.parentElement?.querySelector('label');
    }

    renameLabel(label, value) {
      if (!label) return;
      const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.includes(value)) return;
        if (/subscribe|subscription|one[-_ ]?time|send me this order regularly/i.test(node.textContent)) {
          node.textContent = node.textContent.replace(/send me this order regularly|subscribe(?:\s*(?:&|and)\s*save)?|subscription|one[-_ ]?time(?:\s+purchase)?/i, value);
          return;
        }
      }
    }

    renameFrequencyLabel(label, value) {
      if (!label) return;
      const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!node.textContent.trim() || node.parentElement?.closest('select, option')) continue;
        if (node.textContent.trim() !== value) node.textContent = ` ${value} `;
        return;
      }
      label.insertBefore(document.createTextNode(`${value} `), label.firstChild);
    }

    prepareWidget(data) {
      this.widget = this.scope.querySelector('.rc-widget, .rc-container-wrapper, [id^="RechargeWidget_"], [data-recharge-subscription-widget], recharge-subscription-widget');
      if (!this.widget) return;
      this.widget.classList.add('pollards-purchase-options');
      if (this.intro && this.widget.previousElementSibling !== this.intro) this.widget.before(this.intro);
      if (this.intro) this.intro.hidden = false;
      const oneTime = this.purchaseRadio('onetime');
      const subscription = this.purchaseRadio('subscription');
      this.renameLabel(this.labelFor(oneTime), data.strings.oneTime);
      this.renameLabel(this.labelFor(subscription), data.strings.subscribe);
      const frequency = this.widget.querySelector('.rc-selling-plans__dropdown, select[name="selling_plan"], select[id^="sellingPlan"], select[data-plans-dropdown]');
      const frequencyLabel = frequency?.labels?.[0] || this.widget.querySelector('.rc-selling-plans__label');
      this.renameFrequencyLabel(frequencyLabel, data.strings.frequency);
      frequency?.querySelectorAll('option').forEach(option => {
        if (!option.dataset.pollardsInterval) option.dataset.pollardsInterval = option.textContent.trim();
        const label = option.textContent.replace(/^delivery every\s+/i, '');
        if (option.textContent !== label) option.textContent = label;
      });
      if (!this.defaultApplied && oneTime) {
        this.defaultApplied = true;
        if (!this.explicitPlan && !this.purchaseTouched && !data.requiresSellingPlan && !oneTime.checked && !oneTime.disabled) oneTime.click();
      }
    }

    selectedPlan() {
      const fields = [...this.form.elements].filter(input => input.name === 'selling_plan' && !input.disabled);
      const field = fields.find(input => input.type !== 'radio' && input.type !== 'checkbox' && input.value)
        || fields.find(input => input.checked && input.value);
      return field?.value || '';
    }

    validVariant(data) {
      const picker = this.scope.querySelector('product-variants');
      const variantField = [...this.form.elements].find(input => input.name === 'id' && !input.disabled);
      const variant = picker?.currentVariant || data.variant;
      const selectionRequired = this.form.closest('product-form')?.hasAttribute('data-pollards-selection-required')
        || picker?.hasPlaceholderSelection?.() || (picker?.variantRequired && picker?.noVariantSelectedYet);
      if (selectionRequired || !variantField || !variant?.available || String(variant.id) !== String(variantField.value)) return null;
      if (String(data.variant.id) !== String(variant.id)) return null;
      if ((variant.options || []).some(value => /^please select$/i.test(String(value).replace(/\*/g, '').trim()))) return null;
      return variant;
    }

    updateButton(data, subscriptionActive) {
      const button = this.form.querySelector('[data-js-product-add-to-cart]');
      const text = button?.querySelector('[data-js-product-add-to-cart-text]');
      if (!text) return;
      if (subscriptionActive && !button.disabled && button.getAttribute('aria-disabled') !== 'true') {
        if (text.textContent.trim() !== data.strings.addSubscription) text.textContent = data.strings.addSubscription;
      } else if (text.textContent.trim() === data.strings.addSubscription) {
        const picker = this.scope.querySelector('product-variants');
        if (typeof picker?.updateBuyButtons === 'function') picker.updateBuyButtons();
        else text.textContent = data.strings.addToCart;
      }
    }

    update() {
      const data = this.readData();
      if (!data) return;
      this.prepareWidget(data);
      const variant = this.validVariant(data);
      const planId = this.selectedPlan();
      const allocation = data.variant.allocations.find(item => String(item.id) === String(planId));
      const quantityField = [...this.form.elements].find(input => input.name === 'quantity');
      const quantity = quantityField ? Number(quantityField.value) : 1;
      const oneTimeSelected = this.purchaseRadio('onetime')?.checked;
      const subscription = this.purchaseRadio('subscription');
      const subscriptionSelected = subscription ? subscription.checked : Boolean(planId);
      const active = Boolean(variant && allocation && subscriptionSelected && !oneTimeSelected && Number.isInteger(quantity) && quantity > 0);
      this.content.hidden = !active;
      this.updateButton(data, active);
      if (!active) return;
      const options = (variant.options || []).map((value, index) => {
        if (!value || value === 'Default Title') return '';
        return data.optionNames?.[index] ? `${data.optionNames[index]}: ${value}` : value;
      }).filter(Boolean);
      const selectionText = [data.title, ...options, `${data.strings.quantity}: ${quantity}`].filter(Boolean).join(' · ');
      const frequency = this.widget?.querySelector('.rc-selling-plans__dropdown, select[name="selling_plan"], select[id^="sellingPlan"], select[data-plans-dropdown]');
      const selectedInterval = frequency?.selectedOptions?.[0]?.dataset.pollardsInterval
        || frequency?.selectedOptions?.[0]?.textContent.trim()
        || allocation.options?.find(option => /deliver|frequency|interval/i.test(option.name))?.value
        || allocation.name;
      const interval = String(selectedInterval).replace(/^(\d+)\s*-\s*(days?|weeks?|months?|years?)$/i, `${data.strings.frequency} $1 $2`);
      const price = new Intl.NumberFormat(data.locale, { style: 'currency', currency: data.currency }).format(allocation.price * quantity / 100);
      const termsText = `${price} ${data.strings.perOrder} · ${interval}`;
      if (this.selection.textContent !== selectionText) this.selection.textContent = selectionText;
      if (this.terms.textContent !== termsText) this.terms.textContent = termsText;
    }
  }

  customElements.define('pollards-subscription-summary', PollardsSubscriptionSummary);
})();
